<script setup>
/**
 * BrowseOverlay — universal "Browse all places" sheet for custom pass layouts.
 *
 * A full-screen, theme-agnostic browse experience: search + filter chips
 * (category facets, distance, open-now) + a favorites-only toggle over a
 * faithful list of venue cards. Any layout mounts it by passing venues (in the
 * BrowseVenue shape) + a filter `config`; the host owns favorites + selection.
 *
 * Port of the Lovable source `_shared/browse/BrowseOverlay.tsx`, list surface
 * only (the Google-Maps map view is deferred until venues carry real coords —
 * ENG-2205). Consumes --fr-* tokens directly like the other shared/pass parts.
 *
 * venue = {
 *   id, name, subtitle?, badge?, img?, hours?, distanceMi?, openNow?,
 *   facets?: Record<string, string|string[]>, labels?: string[], address?,
 *   featured?, featuredLabel?, featuredColor?, canCheckIn?
 * }
 * config = {
 *   searchPlaceholder?, showProximity?=true, showAvailability?=true,
 *   facets: [{ id, label, multi?=true, options: [{ value, label, icon? }] }]
 * }
 */
import { ref, computed, watch, nextTick } from 'vue'
import {
  X, Search, SlidersHorizontal, Heart, Navigation, Sparkles,
  Footprints, Car, Globe2, Map as MapIcon, List, Volume2, PlayCircle
} from '@lucide/vue'
import OpenStatus from './OpenStatus.vue'
import themes from '@/themes.js'

const props = defineProps({
  open: { type: Boolean, default: false },
  venues: { type: Array, default: () => [] },
  config: { type: Object, default: () => ({ facets: [] }) },
  /** Style object of --fr-* CSS vars (required across the Teleport boundary). */
  theme: { type: Object, default: null },
  title: { type: String, default: 'All places' },
  kicker: { type: String, default: '' },
  /** Set of favorited venue ids (host-owned). */
  favorites: { type: Object, default: () => new Set() },
  openFilters: { type: Boolean, default: false },
  openMap: { type: Boolean, default: false },
  timezone: { type: String, default: null }
})

const emit = defineEmits(['close', 'select', 'toggle-favorite', 'check-in'])

const PROXIMITY_OPTS = [
  { v: 'walk', label: 'Walk', icon: Footprints },
  { v: 'near', label: 'Nearby', icon: Navigation },
  { v: 'drive', label: 'Drive', icon: Car },
  { v: 'any', label: 'Any', icon: Globe2 }
]

const defaultFilters = () => ({
  search: '',
  facets: {},
  proximity: 'any',
  availability: 'any'
})

const filters = ref(defaultFilters())
const showFilters = ref(false)
const favoritesOnly = ref(false)
const viewType = ref('list')

// Reset transient filter UI each time the sheet opens (source parity).
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    filters.value = defaultFilters()
    showFilters.value = props.openFilters
    favoritesOnly.value = false
    viewType.value = props.openMap ? 'map' : 'list'
  }
})

const isFavorite = id => props.favorites?.has(id)
const favCount = computed(() => props.favorites?.size ?? 0)

const filtered = computed(() => {
  const search = filters.value.search.trim().toLowerCase()
  return props.venues.filter((v) => {
    if (favoritesOnly.value && !isFavorite(v.id)) return false
    if (search) {
      const hay = [v.name, v.subtitle, v.address, ...(v.labels ?? [])]
        .filter(Boolean).join(' ').toLowerCase()
      if (!hay.includes(search)) return false
    }
    for (const [facetId, selected] of Object.entries(filters.value.facets)) {
      if (!selected.length) continue
      const value = v.facets?.[facetId]
      const values = Array.isArray(value) ? value : value ? [value] : []
      if (!values.some(val => selected.includes(val))) return false
    }
    if (typeof v.distanceMi === 'number') {
      const max = filters.value.proximity === 'walk' ? 0.5
        : filters.value.proximity === 'near' ? 2
        : filters.value.proximity === 'drive' ? 10 : Infinity
      if (v.distanceMi > max) return false
    }
    if (filters.value.availability === 'open-now' && v.openNow === false) return false
    return true
  })
})

const mapRef = ref(null)
const mapMarkers = computed(() =>
  filtered.value
    .filter(v => v.lat != null && v.lng != null)
    .map(v => ({ id: v.id, position: { lat: parseFloat(v.lat), lng: parseFloat(v.lng) } }))
)
const mapCenter = computed(() => mapMarkers.value[0]?.position || { lat: 0, lng: 0 })
const mapOptions = computed(() => {
  const api = window.google?.maps
  if (!api) return {}
  return { gestureHandling: 'greedy', scrollwheel: false, styles: themes.map?.standard }
})
function fitMap() {
  const inst = mapRef.value
  if (!inst?.$mapPromise) return
  inst.$mapPromise.then(map => {
    const api = window.google?.maps
    if (!api) return
    const pts = mapMarkers.value
    if (pts.length === 1) {
      map.setCenter(pts[0].position)
      map.setZoom(13)
    } else if (pts.length > 1) {
      map.fitBounds(pts.reduce((b, m) => b.extend(m.position), new api.LatLngBounds()))
    }
    api.event.trigger(map, 'resize')
  }).catch(() => {})
}
function onMarkerClick(id) {
  const v = filtered.value.find(x => x.id === id)
  if (v) emit('select', v)
}
watch(mapRef, inst => { if (inst) nextTick(fitMap) })
watch([viewType, mapMarkers], () => { if (viewType.value === 'map') nextTick(fitMap) })

const filterCount = computed(() => {
  const f = filters.value
  const facetCount = Object.values(f.facets).reduce((acc, arr) => acc + arr.length, 0)
  return facetCount
    + (f.search ? 1 : 0)
    + (f.proximity !== 'any' ? 1 : 0)
    + (f.availability !== 'any' ? 1 : 0)
    + (favoritesOnly.value ? 1 : 0)
})

function toggleFacet(facetId, value, multi) {
  const current = filters.value.facets[facetId] ?? []
  let next
  if (!multi) {
    next = current.includes(value) ? [] : [value]
  } else {
    next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
  }
  filters.value = { ...filters.value, facets: { ...filters.value.facets, [facetId]: next } }
}

function resetFilters() {
  filters.value = defaultFilters()
  favoritesOnly.value = false
}

function onSelect(v) { emit('select', v) }
function onToggleFavorite(v) { emit('toggle-favorite', v.id) }
function onCheckIn(v) { emit('check-in', v) }
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="bo"
      data-overlay
      :style="theme"
      role="dialog"
      aria-modal="true"
      aria-label="Browse all places"
    >
      <!-- Header -->
      <div class="bo-head">
        <div class="bo-head__titles">
          <div v-if="kicker" class="bo-head__kicker">{{ kicker }}</div>
          <h2 class="bo-head__title">{{ title }}</h2>
          <div class="bo-head__count">{{ filtered.length }} of {{ venues.length }} places</div>
        </div>
        <button id="browse_close_btn" type="button" class="bo-iconbtn gtm-click" aria-label="Close browse" @click="emit('close')">
          <X :size="16" />
        </button>
      </div>

      <!-- Search + controls -->
      <div class="bo-controls">
        <div class="bo-search">
          <Search :size="12" class="bo-search__icon" />
          <input
            id="browse_search_input"
            :value="filters.search"
            class="bo-search__input"
            :placeholder="config.searchPlaceholder || 'Search places…'"
            @input="filters = { ...filters, search: $event.target.value }"
          />
          <button
            v-if="filters.search"
            type="button"
            class="bo-search__clear"
            aria-label="Clear search"
            @click="filters = { ...filters, search: '' }"
          >
            <X :size="12" />
          </button>
        </div>
        <button
          id="browse_favorites_toggle"
          type="button"
          class="bo-iconbtn bo-iconbtn--toggle gtm-click"
          :class="{ 'is-active': favoritesOnly }"
          :aria-pressed="favoritesOnly"
          aria-label="Show favorites only"
          @click="favoritesOnly = !favoritesOnly"
        >
          <Heart :size="14" :fill="favoritesOnly ? 'currentColor' : 'none'" />
          <span v-if="favCount > 0" class="bo-iconbtn__badge">{{ favCount }}</span>
        </button>
        <button
          id="browse_filters_toggle"
          type="button"
          class="bo-iconbtn bo-iconbtn--toggle gtm-click"
          :class="{ 'is-active': filterCount > 0 }"
          :aria-label="`${filterCount} filters active`"
          @click="showFilters = !showFilters"
        >
          <SlidersHorizontal :size="14" />
          <span v-if="filterCount > 0" class="bo-iconbtn__badge">{{ filterCount }}</span>
        </button>
        <button
          id="browse_view_toggle"
          type="button"
          class="bo-iconbtn bo-iconbtn--toggle gtm-click"
          :aria-label="viewType === 'map' ? 'Show list' : 'Show map'"
          @click="viewType = viewType === 'map' ? 'list' : 'map'"
        >
          <List v-if="viewType === 'map'" :size="14" />
          <MapIcon v-else :size="14" />
        </button>
      </div>

      <!-- Expanded filters -->
      <div v-if="showFilters" class="bo-filters no-scrollbar" data-scrollable>
        <div v-for="facet in config.facets" :key="facet.id" class="bo-filters__group">
          <div class="bo-filters__label">{{ facet.label }}</div>
          <div class="bo-chips">
            <button
              v-for="opt in facet.options"
              :key="opt.value"
              type="button"
              class="bo-chip gtm-click"
              :class="{ 'is-active': (filters.facets[facet.id] ?? []).includes(opt.value) }"
              @click="toggleFacet(facet.id, opt.value, facet.multi !== false)"
            >
              <component :is="opt.icon" v-if="opt.icon" :size="10" />
              {{ opt.label }}
            </button>
          </div>
        </div>

        <div v-if="config.showProximity !== false" class="bo-filters__group">
          <div class="bo-filters__label">Distance</div>
          <div class="bo-chips">
            <button
              v-for="p in PROXIMITY_OPTS"
              :key="p.v"
              type="button"
              class="bo-chip gtm-click"
              :class="{ 'is-active': filters.proximity === p.v }"
              @click="filters = { ...filters, proximity: p.v }"
            >
              <component :is="p.icon" :size="10" />{{ p.label }}
            </button>
          </div>
        </div>

        <div v-if="config.showAvailability !== false" class="bo-filters__group">
          <div class="bo-filters__label">Availability</div>
          <div class="bo-chips">
            <button
              type="button"
              class="bo-chip gtm-click"
              :class="{ 'is-active': filters.availability === 'any' }"
              @click="filters = { ...filters, availability: 'any' }"
            >
              <Globe2 :size="10" />Any time
            </button>
            <button
              type="button"
              class="bo-chip gtm-click"
              :class="{ 'is-active': filters.availability === 'open-now' }"
              @click="filters = { ...filters, availability: 'open-now' }"
            >
              <Sparkles :size="10" />Open now
            </button>
          </div>
        </div>

        <div v-if="filterCount > 0" class="bo-filters__reset">
          <button type="button" class="bo-reset gtm-click" @click="resetFilters">Reset all</button>
        </div>
      </div>

      <!-- Body -->
      <div class="bo-body">
        <div v-if="filtered.length === 0" class="bo-empty">
          <MapIcon :size="24" class="bo-empty__icon" />
          <div class="bo-empty__text">No places match these filters.</div>
          <button type="button" class="bo-empty__btn gtm-click" @click="resetFilters">Clear filters</button>
        </div>

        <div v-else-if="viewType === 'map'" class="bo-map" data-passthrough>
          <GMapMap ref="mapRef" :center="mapCenter" :zoom="12" :options="mapOptions" class="bo-map__canvas">
            <GMapMarker
              v-for="m in mapMarkers"
              :key="m.id"
              :position="m.position"
              :clickable="true"
              @click="onMarkerClick(m.id)"
            />
          </GMapMap>
        </div>

        <div v-else class="bo-list no-scrollbar" data-scrollable>
          <article
            v-for="v in filtered"
            :key="v.id"
            class="bo-card"
            :class="{ 'is-featured': v.featured }"
            :style="v.featured && v.featuredColor ? {
              boxShadow: `0 0 0 2px ${v.featuredColor}, 0 0 0 6px color-mix(in srgb, ${v.featuredColor} 15%, transparent)`
            } : null"
            @click="onSelect(v)"
          >
            <div
              v-if="v.featured"
              class="bo-card__ribbon"
              :style="{ background: v.featuredColor || 'var(--fr-primary, #ff9900)' }"
            >
              <Sparkles :size="10" />{{ v.featuredLabel || 'Featured' }}
            </div>
            <div class="bo-card__row">
              <img v-if="v.img" :src="v.img" alt="" loading="lazy" class="bo-card__thumb" />
              <div class="bo-card__main">
                <div class="bo-card__top">
                  <div class="bo-card__titles">
                    <div class="bo-card__name">{{ v.name }}</div>
                    <div v-if="v.subtitle" class="bo-card__sub">{{ v.subtitle }}</div>
                  </div>
                  <div class="bo-card__actions">
                    <span v-if="v.badge" class="bo-card__badge">{{ v.badge }}</span>
                    <button
                      type="button"
                      class="bo-fav gtm-click"
                      :class="{ 'is-active': isFavorite(v.id) }"
                      :aria-pressed="isFavorite(v.id)"
                      :aria-label="isFavorite(v.id) ? 'Remove from favorites' : 'Add to favorites'"
                      @click.stop="onToggleFavorite(v)"
                    >
                      <Heart :size="12" :fill="isFavorite(v.id) ? 'currentColor' : 'none'" />
                    </button>
                  </div>
                </div>
                <div class="bo-card__meta">
                  <span v-if="typeof v.distanceMi === 'number'" class="bo-card__metaitem">
                    <Navigation :size="10" />{{ v.distanceMi }} mi
                  </span>
                  <span v-if="v.video || v.audio" class="bo-card__metaitem bo-card__media" :aria-label="v.video ? 'Has video' : 'Has audio tour'">
                    <PlayCircle v-if="v.video" :size="11" />
                    <Volume2 v-if="v.audio" :size="11" />
                  </span>
                  <OpenStatus v-if="v.hours" :weekly="v.hoursWeekly" :hours-string="v.hours" :timezone="timezone" />
                </div>
              </div>
            </div>
            <button
              v-if="v.canCheckIn"
              :id="`browse_checkin_${v.id}`"
              type="button"
              class="bo-card__cta gtm-click"
              @click.stop="onCheckIn(v)"
            >
              Check in to enter
            </button>
          </article>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

.bo {
  position: fixed;
  inset: 0;
  z-index: 2350; // above the pass + FilterPill (2300), below PlaceDetailSheet (2400) so tapping a card opens details on top
  margin: 0 auto;
  max-width: var(--project-max-width, 480px);
  display: flex;
  flex-direction: column;
  background: var(--fr-bg, #f6f4ef);
  color: var(--fr-ink, #1c1c1e);
  font-family: var(--fr-font-display, inherit);
  animation: bo-fade 150ms ease both;
}

/* ---------- Header ---------- */
.bo-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px 8px;
  border-bottom: 1px solid color-mix(in srgb, var(--fr-ink, #1c1c1e) 10%, transparent);
}
.bo-head__titles { flex: 1; min-width: 0; }
.bo-head__kicker {
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  color: var(--fr-muted, #6b7280);
}
.bo-head__title {
  margin: 0;
  font-family: var(--fr-font-serif-display, Georgia, serif);
  font-size: 18px;
  font-weight: 900;
  font-style: italic;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bo-head__count { font-size: 10px; color: var(--fr-muted, #6b7280); }

/* ---------- Icon buttons ---------- */
.bo-iconbtn {
  position: relative;
  display: grid;
  place-items: center;
  height: 32px;
  width: 32px;
  flex-shrink: 0;
  border: 0;
  border-radius: 9999px;
  background: var(--fr-surface, #ffffff);
  color: var(--fr-ink, #1c1c1e);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--fr-ink, #1c1c1e) 15%, transparent);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &--toggle { color: var(--fr-muted, #6b7280); }
  &.is-active {
    background: var(--fr-primary, #ff9900);
    color: var(--fr-primary-foreground, #ffffff);
    box-shadow: none;
  }
}
.bo-iconbtn__badge {
  position: absolute;
  top: -4px;
  right: -4px;
  display: grid;
  place-items: center;
  height: 16px;
  min-width: 16px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--fr-primary, #232f3e);
  color: var(--fr-primary-foreground, #ffffff);
  font-size: 9px;
  font-weight: 900;
  line-height: 1;
}

/* ---------- Search ---------- */
.bo-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
}
.bo-search {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  background: var(--fr-surface, #ffffff);
  padding: 6px 10px;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--fr-ink, #1c1c1e) 12%, transparent);
}
.bo-search__icon { color: var(--fr-muted, #6b7280); flex-shrink: 0; }
.bo-search__input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  outline: none;
  font: inherit;
  font-size: 12px;
  color: var(--fr-ink, #1c1c1e);
  &::placeholder { color: var(--fr-muted, #6b7280); opacity: 0.7; }
}
.bo-search__clear {
  border: 0;
  background: none;
  color: var(--fr-muted, #6b7280);
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;
}

/* ---------- Filters ---------- */
.bo-filters {
  border-top: 1px solid color-mix(in srgb, var(--fr-ink, #1c1c1e) 8%, transparent);
  padding: 8px 16px;
  max-height: 40%;
  overflow-y: auto;
  > * + * { margin-top: 8px; }
}
.bo-filters__label {
  margin-bottom: 4px;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fr-muted, #6b7280);
}
.bo-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.bo-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 9999px;
  padding: 3px 8px;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: var(--fr-surface, #ffffff);
  color: var(--fr-muted, #6b7280);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--fr-ink, #1c1c1e) 12%, transparent);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &.is-active {
    background: var(--fr-primary, #ff9900);
    color: var(--fr-primary-foreground, #ffffff);
    box-shadow: none;
  }
}
.bo-filters__reset { display: flex; justify-content: flex-end; padding-top: 4px; }
.bo-reset {
  border: 0;
  background: none;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fr-muted, #6b7280);
  cursor: pointer;
}

/* ---------- Body ---------- */
.bo-body { flex: 1; min-height: 0; overflow: hidden; }
.bo-map { height: 100%; width: 100%; }
.bo-map__canvas { height: 100%; width: 100%; }
.bo-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
  border-top: 1px solid color-mix(in srgb, currentColor 10%, transparent);
}
.bo-empty__icon { color: var(--fr-muted, #6b7280); margin-bottom: 8px; }
.bo-empty__text { font-size: 12px; color: var(--fr-muted, #6b7280); }
.bo-empty__btn {
  margin-top: 8px;
  border: 0;
  border-radius: 9999px;
  padding: 4px 12px;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: var(--fr-primary, #ff9900);
  color: var(--fr-primary-foreground, #ffffff);
  cursor: pointer;
}
.bo-list {
  height: 100%;
  overflow-y: auto;
  padding: 8px 16px 24px;
  > * + * { margin-top: 8px; }
}

/* ---------- Card ---------- */
.bo-card {
  position: relative;
  border-radius: 12px;
  background: var(--fr-surface, #ffffff);
  padding: 10px;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--fr-ink, #1c1c1e) 10%, transparent);
  cursor: pointer;
  transition: box-shadow 0.15s;

  &.is-featured { margin-top: 14px; } // room for the overhanging ribbon
}
.bo-card__ribbon {
  position: absolute;
  top: -8px;
  left: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 9999px;
  padding: 2px 6px;
  font-family: var(--fr-font-display, inherit);
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #ffffff;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.15);
}
.bo-card__row { display: flex; gap: 10px; }
.bo-card__thumb {
  height: 56px;
  width: 56px;
  flex-shrink: 0;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}
.bo-card__main { min-width: 0; flex: 1; }
.bo-card__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 4px; }
.bo-card__titles { min-width: 0; flex: 1; }
.bo-card__name {
  font-family: var(--fr-font-serif-display, Georgia, serif);
  font-size: 14px;
  font-weight: 700;
  font-style: italic;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bo-card__sub {
  font-size: 11px;
  color: var(--fr-muted, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bo-card__actions { display: flex; flex-shrink: 0; align-items: center; gap: 4px; }
.bo-card__badge {
  border-radius: 9999px;
  padding: 2px 6px;
  font-family: var(--fr-font-display, inherit);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: color-mix(in srgb, var(--fr-accent, #232f3e) 15%, transparent);
  color: var(--fr-accent, #232f3e);
}
.bo-fav {
  display: grid;
  place-items: center;
  height: 24px;
  width: 24px;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: var(--fr-muted, #6b7280);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--fr-ink, #1c1c1e) 15%, transparent);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &.is-active {
    background: var(--fr-primary, #ff9900);
    color: var(--fr-primary-foreground, #ffffff);
    box-shadow: none;
  }
}
.bo-card__meta {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  font-size: 10px;
  color: var(--fr-muted, #6b7280);
}
.bo-card__metaitem { display: inline-flex; align-items: center; gap: 2px; }
.bo-card__media { gap: 3px; color: var(--fr-primary, #ff9900); }
.bo-card__cta {
  margin-top: 8px;
  width: 100%;
  border: 0;
  border-radius: 9999px;
  padding: 8px 0;
  font-family: var(--fr-font-display, inherit);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: var(--fr-primary, #ff9900);
  color: var(--fr-accent, #232f3e);
  cursor: pointer;
  &:active { transform: scale(0.98); }
}

@keyframes bo-fade { from { opacity: 0; } to { opacity: 1; } }
</style>
